/** 薪酬阶段3 · 团队薪酬看板 / 发薪台账（payroll:view）。
 *  可见层级后端按 actor.role 行级过滤：manager 仅本团队 / director(含finance) · admin 全销售。金额为【应发】。 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import { PageHeader, Btn } from '../../components/ui';
import { useAuthStore } from '../../store/auth';
import { useHasPermission } from '../../lib/permissions';
import { toast } from '../../lib/toast';

const yuan = (n: number) => `¥${Number(n || 0).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export function TeamBoardPage() {
  const [month, setMonth] = useState(dayjs().subtract(1, 'month').format('YYYY-MM'));
  const canExport = useHasPermission('payroll:export');
  const { data, isLoading } = useQuery<any>({
    queryKey: ['team-board', month],
    queryFn: () => $.get(`/salary/team-board?month=${month}`),
  });
  const rows: any[] = data?.rows ?? [];
  const totals = rows.reduce(
    (a, r) => ({ base: a.base + Number(r.baseSalary), comm: a.comm + Number(r.commissionAmount), tot: a.tot + Number(r.totalPayable) }),
    { base: 0, comm: 0, tot: 0 },
  );

  const qc = useQueryClient();
  const canRunPayroll = useHasPermission('system:salary_manage');
  const [running, setRunning] = useState(false);
  // bug收尾批#6：admin/财务补跑该月台账(★system:salary_manage 权限点 gate、非角色白名单★)。
  //   口径：必须先 salary-grade/run 定级、再 payroll-ledger/run 取上一步 grade_change_log 出台账；两步均幂等、系统只记台账不打款。
  const onRerun = async () => {
    if (running) return;
    if (!window.confirm(`补跑 ${month} 台账？\n将先重算职级(salary-grade)、再生成发薪台账(payroll-ledger)。\n两步均幂等可重复跑；系统只记台账、不打款。`)) return;
    setRunning(true);
    try {
      await $.post('/salary-grade/run', { month });          // ★先定级★
      await $.post('/salary/payroll-ledger/run', { month });  // ★再台账(读上一步 grade_change_log)★
      toast.success(`${month} 台账已补跑`);
      qc.invalidateQueries({ queryKey: ['team-board', month] });
    } catch (e: any) {
      toast.error(e?.message ?? '补跑失败');
    } finally {
      setRunning(false);
    }
  };

  const onExport = async () => {
    try {
      const token = useAuthStore.getState().accessToken;
      const r = await fetch(`/api/salary/payroll-ledger/export.xlsx?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`导出失败 ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `发薪台账-${month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      toast.error(e?.message ?? '导出失败');
    }
  };

  const th: any = { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text-3)' };
  const thL: any = { ...th, textAlign: 'left' };
  const td: any = { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid var(--bg-3)', fontVariantNumeric: 'tabular-nums' };
  const tdL: any = { ...td, textAlign: 'left' };

  return (
    <>
      <PageHeader
        breadcrumb={['HuanKe', '薪酬看板']}
        title="薪酬看板 / 发薪台账"
        subtitle={`${data?.scope ?? ''} · ${month} · 金额为【应发】（系统记台账不打款）`}
        actions={
          <>
            {canRunPayroll && (
              <Btn kind="default" onClick={onRerun} disabled={running}>
                {running ? '补跑中…' : '🔄 补跑该月台账'}
              </Btn>
            )}
            {canExport && rows.length > 0 && <Btn kind="primary" onClick={onExport}>导出 Excel</Btn>}
          </>
        }
      />
      <div style={{ padding: '0 0 12px' }}>
        <input
          type="month" value={month} max={dayjs().format('YYYY-MM')}
          onChange={(e) => setMonth(e.target.value || month)}
          style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
      </div>
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</div>
      ) : !data?.settled ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
          该月台账未结算（每月 1 号 05:00 自动生成上月台账；admin 可在后台手动补跑）。
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thL}>姓名</th><th style={thL}>职级</th>
                <th style={th}>底薪</th><th style={th}>应发提成</th><th style={th}>应发合计</th>
                <th style={th}>单量</th><th style={th}>业绩</th><th style={thL}>计划发放日</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td style={tdL}>{r.realName}</td>
                  <td style={tdL}>{r.gradeLabel ?? r.salaryGrade}</td>
                  <td style={td}>{yuan(r.baseSalary)}</td>
                  <td style={td}>{yuan(r.commissionAmount)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{yuan(r.totalPayable)}</td>
                  <td style={td}>{r.dealCount}</td>
                  <td style={td}>{yuan(r.perfAmount)}</td>
                  <td style={tdL}>{r.planPayDate ?? '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td style={{ ...tdL, borderTop: '2px solid var(--border)' }} colSpan={2}>合计 {rows.length} 人</td>
                <td style={{ ...td, borderTop: '2px solid var(--border)' }}>{yuan(totals.base)}</td>
                <td style={{ ...td, borderTop: '2px solid var(--border)' }}>{yuan(totals.comm)}</td>
                <td style={{ ...td, borderTop: '2px solid var(--border)' }}>{yuan(totals.tot)}</td>
                <td style={{ ...td, borderTop: '2px solid var(--border)' }} colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
        发薪周期押约一个半月、每周一 18:00 发现金（业务约定）；「计划发放日」为提示，系统不打款。
      </div>
    </>
  );
}
